/**
 * Compile-time public env vars. Vite exposes anything prefixed with
 * VITE_ to client code. The deploy workflow writes a tiny config script
 * (`/config.js`) at deploy time with runtime-resolved Cognito values, so
 * we read those off `window.__SM_CONFIG__` to avoid baking per-stage
 * values into the bundle.
 */

declare global {
  interface Window {
    __SM_CONFIG__?: {
      apiBaseUrl: string;
      cognitoDomain: string;
      cognitoClientId: string;
      cognitoUserPoolId: string;
      cognitoRegion: string;
      stripePublishableKey?: string;
    };
  }
}

export interface RuntimeConfig {
  apiBaseUrl: string;
  /**
   * Cognito Hosted UI domain (e.g. `sm-prod-1234.auth.us-east-1.amazoncognito.com`).
   * Populated by the deploy when GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET are
   * set — empty string otherwise, which the SPA reads as "Google sign-in
   * not configured" and renders the SsoRow button as Coming soon.
   */
  cognitoDomain: string;
  cognitoClientId: string;
  cognitoUserPoolId: string;
  cognitoRegion: string;
  /**
   * Stripe publishable key (`pk_…`) for client-side Stripe Elements. Populated
   * by the deploy when STRIPE_PK_API_KEY is set — empty/undefined otherwise,
   * which the billing UI reads as "billing not available" and declines to
   * mount Elements rather than initializing Stripe against an empty key.
   */
  stripePublishableKey?: string;
}

const FALLBACK: RuntimeConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? "/api",
  cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN ?? "",
  cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? "",
  cognitoUserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "",
  cognitoRegion: import.meta.env.VITE_COGNITO_REGION ?? "us-east-1",
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "",
};

export function getConfig(): RuntimeConfig {
  if (typeof window !== "undefined" && window.__SM_CONFIG__) {
    return window.__SM_CONFIG__;
  }
  return FALLBACK;
}
